import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { IconButton, Paper, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined";

interface Props {
  metric: string;
  comment: string;
}

export default function Glossary({ metric, comment }: Props) {
  const formattedComment = comment
    .split(/(\$|\d+(\.\d{1,2})?)/)
    .map((part, index) =>
      /(\$|\d+(\.\d{1,2})?)/.test(part) ? (
        <span key={index} style={{ color: "#9C27B0", fontWeight: "400" }}>
          {part !== "$" ? Number(part).toLocaleString() : part}
        </span>
      ) : (
        part
      )
    );

  const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const getInisghtTrackerID = (metric: any) => {
    switch (metric) {
      case "PV":
        return "87";
      case "PROFIT":
        return "81";
      case "TOTAL INCOME":
        return "80";
      case "RS INCOME":
        return "79";
      case "CPA INCOME":
        return "78";
      case "CLICKS":
        return "75";
      case "SPEND":
        return "74";
      case "CPC":
        return "82";
      case "FTD":
        return "77";
      case "QFTD":
        return "71";
      case "CVR":
        return "76";
      case "CAC":
        return "83";
      case "NRPU":
        return "84";
      case "CPA":
        return "78";
      case "RS":
        return "79";
      case "CPA PV":
        return "85";
      case "RS PV":
        return "86";
      default:
        return "61";
    }
  };

  const GlossaryList = (
    <Box sx={{ width: 260 }} role="presentation" onClick={toggleDrawer(false)}>
      <Typography
        variant="h6"
        px={2}
        pt={1}
        color={"#000000DE"}
        mb={1}
        fontSize={"20px"}
        fontWeight={"500"}
      >
        Insight
      </Typography>

      <Paper
        sx={{ boxShadow: "none", overflowY: "auto", height: "90vh", p: 2 }}
      >
        {metric === "PV" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              PV - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              The Player Value metric, also known as Customer Value or Revenue
              per Customer, represents the average income generated per acquired
              customer. It is typically calculated by dividing the total income
              by the number of acquired customers (FTDs)
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "PROFIT" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              PROFIT - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              Total income minus all associated costs, including taxes, to
              reflect the net earnings. This calculation provides a clear
              picture of the actual profitability after accounting for all
              expenses.
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "TOTAL INCOME" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              TOTAL INCOME - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              The Total Income (CPA Income + Revshare Income) represents the
              combined revenue generated from both Cost Per Acquisition (CPA)
              and Revenue Share (Revshare) agreements, expressed in dollar
              currency.
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "RS INCOME" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              RS INCOME - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              The Amount of Income from Revenue Share represents the total
              income generated through revenue-sharing agreements, expressed in
              dollar currency. This metric reflects the earnings derived from
              partnerships or agreements where a portion of the revenue is
              shared based on specific terms.
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "CPA INCOME" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              CPA INCOME - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              The Amount of Income from CPA (Cost Per Acquisition) represents
              the total income generated through CPA agreements, where income is
              earned based on each new customer acquired. This metric is viewed
              from the operator's perspective, focusing on revenue brought in
              through customer acquisition models.
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "CLICKS" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              CLICKS - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              The Click In metric tracks when a user enters our website from an
              external advertising platform, such as Google, Bing, or other
              similar sources
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "SPEND" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              SPEND - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              Total amount expended on advertising, encompassing both the base
              advertising costs and any additional tax percentage applicable to
              your account
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "CPC" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              CPC - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              Cost Per Click - How much We paid for each click on average.
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "FTD" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              FTD - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              First Time Deposit - An action where the user had made it’s First
              Time Deposit (No matter the amount).
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "QFTD" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              QFTD - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              Qualified First Time Deposit - In some cases, there is a threshold
              which the user must go through in order to receive the CPA.
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "CVR" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              CVR - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              Conversion Rate - Conversion between the amount of Clicks to the
              amount of FTDs - (ftd/clicks).
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "CAC" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              CAC - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              Conversion Acquisition Cost - How much we paid for each Conversion
              on average - (Spend/ftd).
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "NRPU" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              NRPU - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              Net Revenue Per User - How much the brand have gained in Revenue
              from each user, on average - (Net Revenue/Active Users)
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "CPA" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              CPA - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              Cost Per Acquisition - The Amount of Income we are paid from the
              brands, for total of qftd.
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "RS" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              RS - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              Revenue Share - The Amount of Income we are paid from the brands,
              as a share of their Net Revenue (Net Revenue * deal%)
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "CPA PV" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              CPA PV - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              cpa per user - Average (cpa/ftd).
            </Typography>
          </Stack>
        ) : (
          ""
        )}

        {metric === "RS PV" ? (
          <Stack>
            <Typography sx={{ fontSize: "16px" }} variant="body1">
              {formattedComment}
            </Typography>
            <Typography
              sx={{ color: "#7B1FA2", fontWeight: "500", fontSize: "18px" }}
              mb={0.5}
              mt={3}
              variant="body1"
            >
              RS PV - Definition
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "400" }}
              variant="body1"
            >
              rs per user → Average (rs/Active Users).
            </Typography>
          </Stack>
        ) : (
          ""
        )}
      </Paper>
    </Box>
  );

  return (
    <div>
      <IconButton
        onClick={toggleDrawer(true)}
        color="secondary"
        sx={{ p: 1 }}
        data-tracker={getInisghtTrackerID(metric)}
      >
        <EmojiObjectsOutlinedIcon color="secondary" />
      </IconButton>

      <Drawer
        sx={{ zIndex: 9999 }}
        open={open}
        anchor={"right"}
        onClose={toggleDrawer(false)}
      >
        {GlossaryList}
      </Drawer>
    </div>
  );
}
